// =====================================================================
// ADMIN · COSTOS (privado, solo admin)
//
// Costo REAL de proveedor por SKU (distinto del precio de venta), factor
// logistico por producto, factor de importacion global, y landed cost.
// Datos en product_costs (RLS is_admin). Referencia anti-alza = ultimo
// costo de compra (se llena solo al recibir POs).
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';

type Row = {
  product_id: string;
  sku: string;
  name: string;
  brand_code: string;
  category_root: string | null;
  supplier_cost: number | null;
  cost_currency: string;
  logistics_factor_pct: number;
  supplier_id: string | null;
  supplier_name: string | null;
  import_factor_pct: number;
  last_purchase_cost: number | null;
  last_purchase_currency: string | null;
  last_purchase_at: string | null;
};

export function CostosPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string; import_factor_pct: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [brand, setBrand] = useState<'all' | 'nuled' | 'lutron'>('all');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [supFilter, setSupFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [showImport, setShowImport] = useState(false);
  const [draft, setDraft] = useState<Record<string, { cost: string; cur: string; log: string; sup: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSup, setBulkSup] = useState('');
  const [bulkLog, setBulkLog] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const supMap = useMemo(() => {
    const m = new Map<string, { name: string; imp: number }>();
    for (const s of suppliers) m.set(s.id, { name: s.name, imp: Number(s.import_factor_pct ?? 20) });
    return m;
  }, [suppliers]);

  const catRoots = useMemo(() => Array.from(new Set(rows.map(r => r.category_root).filter(Boolean) as string[])).sort(), [rows]);

  const load = async () => {
    setLoading(true);
    const [prodRes, costRes, supRes] = await Promise.all([
      supabase.from('products').select('id, sku, name, brand_code, category_paths, status').eq('status', 'active').order('sku').limit(3000),
      supabase.from('product_costs').select('product_id, variant_id, supplier_cost, cost_currency, logistics_factor_pct, supplier_id, last_purchase_cost, last_purchase_currency, last_purchase_at').is('variant_id', null),
      supabase.from('suppliers').select('id, name, import_factor_pct').eq('active', true).order('name'),
    ]);
    const sups = ((supRes.data as any[]) ?? []).map(x => ({ id: x.id as string, name: x.name as string, import_factor_pct: Number(x.import_factor_pct ?? 20) }));
    setSuppliers(sups);
    const sName = new Map<string, string>();
    const sImp = new Map<string, number>();
    for (const x of sups) { sName.set(x.id, x.name); sImp.set(x.id, x.import_factor_pct); }
    const costMap = new Map<string, any>();
    for (const c of (costRes.data as any[]) ?? []) costMap.set(c.product_id, c);
    const list: Row[] = ((prodRes.data as any[]) ?? []).map(p => {
      const c = costMap.get(p.id);
      const sid = c?.supplier_id ?? null;
      const paths = (p.category_paths ?? []) as string[];
      const catRoot = paths.length ? String(paths[0]).split('/')[0] : null;
      return {
        product_id: p.id, sku: p.sku, name: p.name, brand_code: p.brand_code, category_root: catRoot,
        supplier_cost: c?.supplier_cost ?? null,
        cost_currency: c?.cost_currency ?? (p.brand_code === 'lutron' ? 'USD' : 'MXN'),
        logistics_factor_pct: c?.logistics_factor_pct ?? 0,
        supplier_id: sid,
        supplier_name: sid ? (sName.get(sid) ?? null) : null,
        import_factor_pct: sid ? (sImp.get(sid) ?? 20) : 20,
        last_purchase_cost: c?.last_purchase_cost ?? null,
        last_purchase_currency: c?.last_purchase_currency ?? null,
        last_purchase_at: c?.last_purchase_at ?? null,
      };
    });
    setRows(list);
    const d: Record<string, { cost: string; cur: string; log: string; sup: string }> = {};
    for (const r of list) d[r.product_id] = {
      cost: r.supplier_cost != null ? String(r.supplier_cost) : '',
      cur: r.cost_currency, log: r.logistics_factor_pct ? String(r.logistics_factor_pct) : '', sup: r.supplier_id ?? '',
    };
    setDraft(d);
    setSelected(new Set());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const landed = (cost: number | null, log: number, imp: number) =>
    cost == null ? null : Math.round(cost * (1 + log / 100) * (1 + imp / 100) * 100) / 100;

  const filtered = useMemo(() => rows.filter(r => {
    if (brand !== 'all' && r.brand_code !== brand) return false;
    if (supFilter === 'none' ? r.supplier_id != null : (supFilter !== 'all' && r.supplier_id !== supFilter)) return false;
    if (catFilter !== 'all' && r.category_root !== catFilter) return false;
    if (onlyMissing && r.supplier_cost != null) return false;
    if (!q.trim()) return true;
    const qn = q.trim().toLowerCase();
    return r.sku.toLowerCase().includes(qn) || r.name.toLowerCase().includes(qn);
  }), [rows, brand, supFilter, catFilter, onlyMissing, q]);

  const shown = filtered.slice(0, 400);
  const conCosto = rows.filter(r => r.supplier_cost != null).length;
  const allShownSelected = shown.length > 0 && shown.every(r => selected.has(r.product_id));

  function toggle(id: string) {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAll() {
    setSelected(prev => {
      const n = new Set(prev);
      if (allShownSelected) shown.forEach(r => n.delete(r.product_id));
      else shown.forEach(r => n.add(r.product_id));
      return n;
    });
  }

  const setD = (pid: string, patch: Partial<{ cost: string; cur: string; log: string; sup: string }>) =>
    setDraft(prev => ({ ...prev, [pid]: { ...prev[pid], ...patch } }));

  async function saveField(pid: string) {
    const d = draft[pid];
    if (!d) return;
    setSavingId(pid);
    const { error } = await supabase.rpc('upsert_product_cost', {
      p_product_id: pid, p_variant_id: null,
      p_supplier_cost: d.cost === '' ? null : Number(d.cost),
      p_currency: d.cur, p_logistics_pct: d.log === '' ? 0 : Number(d.log),
      p_notes: null, p_supplier_id: d.sup || null,
    });
    setSavingId(null);
    if (error) { alert(error.message); return; }
    setRows(prev => prev.map(r => r.product_id === pid ? {
      ...r,
      supplier_cost: d.cost === '' ? null : Number(d.cost),
      cost_currency: d.cur,
      logistics_factor_pct: d.log === '' ? 0 : Number(d.log),
      supplier_id: d.sup || null,
      supplier_name: d.sup ? (supMap.get(d.sup)?.name ?? null) : null,
      import_factor_pct: d.sup ? (supMap.get(d.sup)?.imp ?? 20) : 20,
    } : r));
  }

  async function applyBulk(kind: 'supplier' | 'logistics') {
    if (selected.size === 0) return;
    if (kind === 'supplier' && !bulkSup) return;
    if (kind === 'logistics' && bulkLog === '') return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    const { error } = kind === 'supplier'
      ? await supabase.rpc('bulk_set_product_supplier', { p_product_ids: ids, p_supplier_id: bulkSup })
      : await supabase.rpc('bulk_set_product_logistics', { p_product_ids: ids, p_pct: Number(bulkLog) });
    setBulkBusy(false);
    if (error) { alert(error.message); return; }
    setBulkSup(''); setBulkLog('');
    await load();
  }

  return (
    <div>
      <div style={{ background: theme.bgFooter, borderLeft: `4px solid ${theme.accent}`, padding: 12, marginBottom: 16, fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
        🔒 <strong>Costos privados</strong> — costo real de proveedor (lo que pagas), no el precio de venta. Edita costo, moneda, % logística y proveedor <strong>directo en la tabla</strong> (se guarda al salir del campo).
        Landed = costo × (1 + % logística) × (1 + % importación del proveedor).
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Buscar SKU o nombre…" value={q} onChange={e => setQ(e.target.value)}
          style={{ flex: 1, minWidth: 220, padding: '9px 12px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font.sans, outline: 'none' }} />
        <select value={brand} onChange={e => setBrand(e.target.value as any)} style={selStyle}>
          <option value="all">Todas las marcas</option>
          <option value="nuled">NULED</option>
          <option value="lutron">Lutron</option>
        </select>
        <select value={supFilter} onChange={e => setSupFilter(e.target.value)} style={selStyle}>
          <option value="all">Todos los proveedores</option>
          <option value="none">Sin proveedor</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={selStyle}>
          <option value="all">Todas las categorías</option>
          {catRoots.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.brand, cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} />
          Solo sin costo
        </label>
        <button onClick={() => setShowImport(true)} style={btn}>⬆ Importar CSV</button>
      </div>

      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: '10px 14px', background: theme.bgSoft, border: `1px solid ${theme.accent}` }}>
          <strong style={{ fontSize: 13, color: theme.brand }}>{selected.size} seleccionados</strong>
          <select value={bulkSup} onChange={e => setBulkSup(e.target.value)} style={selStyle}>
            <option value="">— proveedor —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={() => applyBulk('supplier')} disabled={!bulkSup || bulkBusy} style={{ ...btn, opacity: !bulkSup || bulkBusy ? 0.5 : 1 }}>Asignar proveedor</button>
          <span style={{ color: theme.textMuted }}>|</span>
          <input type="number" step="0.1" placeholder="% log" value={bulkLog} onChange={e => setBulkLog(e.target.value)}
            style={{ width: 70, padding: '7px 8px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, textAlign: 'right' }} />
          <button onClick={() => applyBulk('logistics')} disabled={bulkLog === '' || bulkBusy} style={{ ...btn, opacity: bulkLog === '' || bulkBusy ? 0.5 : 1 }}>Aplicar % logística</button>
          <button onClick={() => setSelected(new Set())} style={btnGhost}>Limpiar</button>
        </div>
      )}

      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 8 }}>
        {loading ? 'Cargando…' : `${filtered.length} de ${rows.length} · ${conCosto} con costo capturado`}{savingId ? ' · guardando…' : ''}
      </div>

      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} rows={rows} />}

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1120 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th style={{ width: 34 }}><input type="checkbox" checked={allShownSelected} onChange={toggleAll} /></Th>
              <Th>SKU / Producto</Th>
              <Th style={{ width: 160 }}>Proveedor</Th>
              <Th style={{ width: 150 }}>Costo proveedor</Th>
              <Th style={{ textAlign: 'right', width: 90 }}>% Logística</Th>
              <Th style={{ textAlign: 'right', width: 70 }}>% Import</Th>
              <Th style={{ textAlign: 'right', width: 120 }}>Landed</Th>
              <Th style={{ textAlign: 'right', width: 130 }}>Última compra</Th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => {
              const d = draft[r.product_id] ?? { cost: '', cur: r.cost_currency, log: '', sup: '' };
              const imp = d.sup ? (supMap.get(d.sup)?.imp ?? 20) : 20;
              const l = landed(d.cost === '' ? null : Number(d.cost), d.log === '' ? 0 : Number(d.log), imp);
              return (
                <tr key={r.product_id} style={{ borderBottom: `1px solid ${theme.border}`, background: selected.has(r.product_id) ? theme.bgSoft : 'transparent' }}>
                  <Td><input type="checkbox" checked={selected.has(r.product_id)} onChange={() => toggle(r.product_id)} /></Td>
                  <Td>
                    <div style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{r.sku}</div>
                    <div style={{ fontSize: 12, color: theme.brand }}>{r.name}</div>
                    <div style={{ fontSize: 9, color: theme.textMuted, textTransform: 'uppercase' }}>{r.brand_code}</div>
                  </Td>
                  <Td>
                    <select value={d.sup} onChange={e => { setD(r.product_id, { sup: e.target.value }); }} onBlur={() => saveField(r.product_id)}
                      style={{ width: '100%', padding: '5px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 11 }}>
                      <option value="">— sin proveedor —</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="number" step="0.01" placeholder="costo" value={d.cost}
                        onChange={e => setD(r.product_id, { cost: e.target.value })} onBlur={() => saveField(r.product_id)}
                        style={{ width: 80, padding: '5px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, textAlign: 'right' }} />
                      <select value={d.cur} onChange={e => { setD(r.product_id, { cur: e.target.value }); }} onBlur={() => saveField(r.product_id)}
                        style={{ padding: '5px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 11 }}>
                        <option value="USD">USD</option><option value="MXN">MXN</option>
                      </select>
                    </div>
                  </Td>
                  <Td style={{ textAlign: 'right' }}>
                    <input type="number" step="0.1" placeholder="%" value={d.log}
                      onChange={e => setD(r.product_id, { log: e.target.value })} onBlur={() => saveField(r.product_id)}
                      style={{ width: 58, padding: '5px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, textAlign: 'right' }} />
                  </Td>
                  <Td style={{ textAlign: 'right', color: theme.textMuted }}>{d.sup ? `${imp}%` : '—'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 700, color: theme.brand }}>
                    {l != null ? `$${l.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${d.cur}` : '—'}
                  </Td>
                  <Td style={{ textAlign: 'right', color: theme.textMuted }}>
                    {r.last_purchase_cost != null
                      ? <div><div>${Number(r.last_purchase_cost).toLocaleString('en-US', { maximumFractionDigits: 2 })} {r.last_purchase_currency}</div>
                          <div style={{ fontSize: 9 }}>{r.last_purchase_at ? new Date(r.last_purchase_at).toLocaleDateString('es-MX') : ''}</div></div>
                      : '—'}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onDone, rows }: { onClose: () => void; onDone: () => void; rows: { product_id: string; sku: string }[] }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const skuMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) m.set(r.sku.toLowerCase(), r.product_id);
    return m;
  }, [rows]);

  async function run() {
    setBusy(true); setResult(null);
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    let ok = 0, skip = 0;
    const errs: string[] = [];
    for (const line of lines) {
      const parts = line.split(/[,\t;]/).map(p => p.trim());
      const [sku, cost, cur, log] = parts;
      if (!sku || sku.toLowerCase() === 'sku') { skip++; continue; }
      const pid = skuMap.get(sku.toLowerCase());
      if (!pid) { errs.push(`SKU no encontrado: ${sku}`); skip++; continue; }
      const { error } = await supabase.rpc('upsert_product_cost', {
        p_product_id: pid, p_variant_id: null,
        p_supplier_cost: cost ? Number(cost) : null,
        p_currency: (cur || 'USD').toUpperCase(),
        p_logistics_pct: log ? Number(log) : 0, p_notes: 'import_csv',
      });
      if (error) { errs.push(`${sku}: ${error.message}`); skip++; } else ok++;
    }
    setBusy(false);
    setResult(`✓ ${ok} actualizados · ${skip} omitidos${errs.length ? '\n' + errs.slice(0, 8).join('\n') : ''}`);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: 28, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '0 0 8px' }}>Importar costos (CSV)</h2>
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
          Una línea por SKU: <code style={{ fontFamily: theme.font.mono }}>SKU, costo, moneda, %logística</code>. Ej:<br />
          <code style={{ fontFamily: theme.font.mono, fontSize: 11 }}>ND5-3000K, 12.50, USD, 8</code><br />
          Moneda y % logística son opcionales (default USD y 0). Solo actualiza SKUs existentes.
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder="SKU, costo, moneda, %logística" rows={10}
          style={{ width: '100%', background: theme.bg, border: `1px solid ${theme.border}`, padding: 10, color: theme.text, fontSize: 12, fontFamily: theme.font.mono, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
        {result && <pre style={{ fontSize: 12, color: theme.textMuted, whiteSpace: 'pre-wrap', marginTop: 10 }}>{result}</pre>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnGhost}>Cerrar</button>
          <button onClick={run} disabled={busy || !text.trim()} style={{ ...btn, opacity: busy || !text.trim() ? 0.6 : 1 }}>{busy ? 'Importando…' : 'Importar'}</button>
        </div>
        {result && <div style={{ marginTop: 10 }}><button onClick={onDone} style={btn}>Listo, recargar</button></div>}
      </div>
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, ...style }}>{children}</th>;
}
function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', verticalAlign: 'middle', ...style }}>{children}</td>;
}

const selStyle: React.CSSProperties = { padding: '9px 12px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font.sans, outline: 'none' };
const btn: React.CSSProperties = { background: theme.accent, color: theme.brand, border: 'none', padding: '9px 14px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans };
const btnGhost: React.CSSProperties = { background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans };
